import { FlexCenter } from "@9amhealth/shared";
import type { AppliedCouponResponse } from "@9amhealth/openapi";
import { SubscriptionDetailsResponse } from "@9amhealth/openapi";
import { useBlocNext } from "@blac/react";
import type { FC, ReactNode } from "react";
import React, { useEffect, useMemo, useCallback } from "react";
import { useFocusHeading, getHeadingProps } from "src/lib/useFocusHeading";
import { APP_CONTENT_WIDTH } from "src/constants/layout";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";
import { ConversionEvent } from "src/state/Track/TrackCubit";
import { SubscriptionInterval } from "src/state/TreatmentPlanCubit/TreatmentPlanCubit";
import { toast, tracker, useBloc } from "src/state/state";
import { TranslationKey } from "src/types/translationKey";
import { Legal } from "src/ui/components/Legal/Legal";
import Payment from "src/ui/components/Payment/Payment";
import { PaymentContextWithPaymentCubit } from "src/ui/components/PaymentContext/PaymentContext";
import Price from "src/ui/components/Price/Price";
import CustomErrorBoundary from "src/ui/components/SentryBoundary/CustomErrorBoundary";
import SignupCustomBloc from "src/ui/components/SignupCustomContent/state/SignupCustomBloc";
import TextWithKeywords from "src/ui/components/TextWithKeywords/TextWithKeywords";
import { Trust } from "src/ui/components/Trust/Trust";
import CouponDiscount from "../../CouponDiscount/CouponDiscount";
import {
  CheckoutCartItemKey,
  CheckoutCartItemViewData
} from "../../SignupCustomStepCheckoutDynamicContent/SignupCustomStepCheckoutDynamicContent";
import Translate from "../../Translate/Translate";

export const SignupCustomStepCheckoutRawContent: FC<{
  content: SignupCheckoutPageContentProps;
  showShipping: boolean;
  totalPrice: number;
  totalPriceFirstMonth: number;
  discount?: {
    coupon?: AppliedCouponResponse;
  };
  handleOrder?: () => Promise<boolean>;
  labTestsIncluded: boolean;
  paymentInterval?: SubscriptionInterval;
  subscriptionId?: string;
  cartItems: Record<CheckoutCartItemKey, CheckoutCartItemViewData>;
}> = (props) => {
  const {
    content,
    showShipping,
    totalPrice,
    totalPriceFirstMonth,
    discount,
    cartItems
  } = props;
  const [
    ,
    {
      filterAllSubscriptions,
      buySubscription,
      setDemoFunnel,
      getSubscriptionById
    }
  ] = useBloc(SubscriptionCubit);
  const [, { nextStep, isDemoFunnel, showSaving, hideSaving }] =
    useBlocNext(SignupCustomBloc);
  const [errorMessage, setErrorMessage] = React.useState<TranslationKey>();
  const showPayment = totalPrice > 0;
  const { headingRef } = useFocusHeading();

  setDemoFunnel(isDemoFunnel);

  const handlePaymentStart = useCallback(() => {
    setErrorMessage(undefined);
    showSaving({
      durationMs: 20000,
      text: translate("savingIndicator.info", { context: "enrolling" })
    });
  }, [showSaving]);

  const funnelSpecificSubscription = useMemo(() => {
    if (props.subscriptionId) {
      return getSubscriptionById(props.subscriptionId);
    }
    return filterAllSubscriptions({
      status: [SubscriptionDetailsResponse.status.DRAFT],
      duration: [SubscriptionInterval.monthly, SubscriptionInterval.quarterly],
      looseCheck: true
    })[0] as SubscriptionDetailsResponse | undefined;
  }, []);

  useEffect(() => {
    if (!funnelSpecificSubscription) {
      reportErrorSentry(
        new Error("Checkout could not find subscription to buy")
      );
      toast.error("error.generic");
    }
  }, [funnelSpecificSubscription]);

  const handleOrder = useCallback(() => {
    setErrorMessage(undefined);
    showSaving({
      durationMs: 20000,
      text: translate("savingIndicator.info", { context: "enrolling" })
    });

    if (props.handleOrder) {
      void props.handleOrder().catch((err: unknown) => {
        reportErrorSentry(err);
        hideSaving();
      });
      return true;
    }

    if (funnelSpecificSubscription) {
      void buySubscription(funnelSpecificSubscription.id, {
        onSuccess: () => {
          tracker.trackConversion({
            event: ConversionEvent.FunnelCheckoutCompleted
          });
          void nextStep();
        },
        onError: () => {
          hideSaving();
          reportErrorSentry(new Error("Error buying subscription"));
          toast.error("error_generic");
          setErrorMessage("error_generic");
        }
      });
    } else {
      window.location.reload();
      return false;
    }

    return true;
  }, [
    props.handleOrder,
    showSaving,
    hideSaving,
    funnelSpecificSubscription,
    buySubscription,
    nextStep
  ]);

  return (
    <FlexCenter
      style={{
        maxWidth: `${APP_CONTENT_WIDTH}px`,
        margin: "0 auto"
      }}
    >
      <nine-spacer s="xl"></nine-spacer>
      <nine-heading>
        <h1 className="as-h3" ref={headingRef} {...getHeadingProps()}>
          {content.title}
        </h1>
        <nine-spacer s="xs"></nine-spacer>
        <p className="m0 color-c-80">{content.subtitle}</p>
      </nine-heading>

      {content.infoSections?.()}

      {content.cartSection && (
        <nine-info-section style={{ margin: "0.8em 0" }}>
          <p
            slot="title"
            className="m0 strong as-tiny"
            style={{ color: "var(--color-charcoal-60)", marginTop: "3rem" }}
          >
            <Translate msg="summary" uppercase={true} />
          </p>
          <nine-info-container slot="content">
            <nine-info-row
              variant="medication"
              imageSrc={content.cartSection.image}
            >
              <h5 slot="title" className="m0 strong">
                {content.programTitle}
              </h5>

              <p slot="description" className="m0 color-c-80">
                <TextWithKeywords text={content.cartSection.subtitle} />
              </p>
            </nine-info-row>

            <nine-table shadow="false" showTitle="false" gradient="false">
              {showShipping && (
                <nine-row px0 pyLarge borderGrayLight>
                  <p slot="title" className="m0 strong">
                    <Translate msg="shipping" />
                  </p>
                  <p slot="label" className="m0 text-right">
                    <Price amount={0} />
                  </p>
                </nine-row>
              )}
              {(cartItems.onboarding.show || cartItems.ongoingCare.show) && (
                <nine-row px0 pyLarge borderGrayLight>
                  {cartItems.onboarding.show && (
                    <>
                      <p slot="title" className="as-little color-c">
                        {cartItems.onboarding.title && (
                          <Translate msg={cartItems.onboarding.title} />
                        )}
                      </p>
                      <div slot="label" style={{ marginBottom: "1rem" }}>
                        <p className="as-little color-c text-right mt0">
                          <Price
                            amount={cartItems.onboarding.fee}
                            formatLocale
                            keepNumber
                          />
                        </p>
                      </div>
                    </>
                  )}

                  {cartItems.ongoingCare.show && (
                    <>
                      <p slot="title" className="m0 as-little color-c">
                        {cartItems.ongoingCare.title && (
                          <Translate msg={cartItems.ongoingCare.title} />
                        )}
                      </p>
                      <div slot="label" className="m0">
                        <p className="m0 as-little color-c text-right">
                          <Price
                            amount={cartItems.ongoingCare.fee}
                            duration={props.paymentInterval}
                            formatLocale
                            keepNumber
                          />
                        </p>
                      </div>
                    </>
                  )}
                </nine-row>
              )}

              {discount?.coupon && (
                <nine-row px0 pyLarge borderGrayLight>
                  <p slot="title" className="m0 strong">
                    {translate(`discount_name`, {
                      context: discount.coupon.duration
                    })}
                  </p>
                  <p slot="label" className="m0 text-right">
                    <CouponDiscount
                      coupon={discount.coupon}
                      totalPrice={totalPrice}
                    />
                  </p>
                </nine-row>
              )}

              <nine-row px0 pyLarge borderGrayLight>
                <h5 slot="title" className="m0 strong color-c">
                  <Translate
                    msg="total"
                    variables={{
                      context:
                        totalPriceFirstMonth === totalPrice
                          ? undefined
                          : "first_month"
                    }}
                  />
                </h5>
                <h5 slot="label" className="m0 strong color-c text-right">
                  <Price
                    amount={totalPriceFirstMonth}
                    formatLocale
                    keepNumber
                  />
                </h5>
              </nine-row>
            </nine-table>
          </nine-info-container>
        </nine-info-section>
      )}

      {showPayment && (
        <PaymentContextWithPaymentCubit>
          <CustomErrorBoundary>
            <Payment
              buyAction={async () => handleOrder()}
              onPaymentStart={handlePaymentStart}
              onError={hideSaving}
              errorMessage={errorMessage}
              subscriptionId={"subscription.id"}
              legal={
                <Legal
                  duration={SubscriptionInterval.monthly}
                  cartItems={cartItems}
                  additionalText={content.legalAppendix}
                  priceToday={totalPriceFirstMonth}
                />
              }
            />
          </CustomErrorBoundary>
        </PaymentContextWithPaymentCubit>
      )}

      {content.showMoneyBackGuarantee && (
        <nine-additional-benefits style={{ marginTop: 0 }}>
          <nine-benefit
            icon="moneyBack"
            content="30-day money back guarantee"
          ></nine-benefit>
        </nine-additional-benefits>
      )}
      <Trust />
    </FlexCenter>
  );
};

export interface InfoSectionProps {
  onEnroll?: () => unknown;
}

export interface SignupCheckoutPageContentCartSection {
  title: ReactNode;
  image?: string;
  subtitle?: string;
  showShipping?: boolean;
  couponCode?: string;
  couponText?: string;
}

export interface SignupCheckoutPageContentProps {
  title: string;
  subtitle?: string;
  infoSections?: () => ReactNode;
  cartSection?: SignupCheckoutPageContentCartSection;
  legalAppendix?: ReactNode;
  showMoneyBackGuarantee?: boolean;
  programTitle?: string;
}
